/**
 * ComparativosMultiSelect — portal-based drop-in replacement for MultiSelectChecklist,
 * scoped exclusively to the Resultados Financieros / Comparativos module.
 *
 * WHY THIS EXISTS:
 * The shared MultiSelectChecklist renders its dropdown as `position: absolute` inside
 * the component's own `relative` div (z-index 20).  The main app shell uses an
 * overflow-constrained layout container, which clips any absolute panel that extends
 * beyond its bounds — causing the dropdown to overlap headers or get its bottom items
 * cut off / unclickable (e.g. "COL VILLAVICENCIO" hidden when filtering by "col").
 *
 * THE FIX (architecture-safe):
 * Render the dropdown panel via ReactDOM.createPortal directly into document.body,
 * positioned with `position: fixed` + getBoundingClientRect() on the trigger button.
 * This escapes ALL overflow and stacking-context constraints without touching the
 * shared component. The flip logic opens the panel upward when there isn't enough
 * space below the trigger in the viewport.
 *
 * Props are identical to MultiSelectChecklist — this is a zero-effort drop-in.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';

// ─── Local Button (matches shared Button "outline" / default variants) ────────
function Btn({
  variant = 'primary',
  onClick,
  children,
}: {
  variant?: 'primary' | 'outline';
  onClick: () => void;
  children: React.ReactNode;
}) {
  const base =
    'flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-brand-200';
  const styles =
    variant === 'outline'
      ? `${base} border border-ink-200 bg-surface text-ink-700 hover:bg-surface-2`
      : `${base} bg-brand-600 text-white hover:bg-brand-700`;
  return (
    <button type="button" className={styles} onClick={onClick}>
      {children}
    </button>
  );
}

// ─── Panel coords (fixed positioning) ─────────────────────────────────────────
interface PanelCoords {
  top: number;
  left: number;
  width: number;
  openUpward: boolean;
}

const PANEL_MAX_H = 280; // px — enough for ~10 items + search + footer
const PANEL_FOOT_H = 52; // approximate height of Cancelar/Aplicar row
const PANEL_HEAD_H = 44; // approximate height of search row
const PANEL_ITEM_H = 34; // approximate height per item
const VIEWPORT_MARGIN = 12; // px — don't hug the viewport edge

function computeCoords(trigger: HTMLButtonElement): PanelCoords {
  const r = trigger.getBoundingClientRect();
  const spaceBelow = window.innerHeight - r.bottom - VIEWPORT_MARGIN;
  const spaceAbove = r.top - VIEWPORT_MARGIN;
  const openUpward = spaceBelow < PANEL_MAX_H && spaceAbove > spaceBelow;
  return {
    top: openUpward ? r.top : r.bottom + 8,
    left: r.left,
    width: Math.max(r.width, 260),
    openUpward,
  };
}

// ─── Dropdown Portal ──────────────────────────────────────────────────────────
function DropdownPortal({
  coords,
  options,
  pending,
  search,
  onSearch,
  onToggleAll,
  onToggleOne,
  onCancel,
  onApply,
  display,
  allChecked,
}: {
  coords: PanelCoords;
  options: string[];
  pending: string[];
  search: string;
  onSearch: (v: string) => void;
  onToggleAll: () => void;
  onToggleOne: (o: string) => void;
  onCancel: () => void;
  onApply: () => void;
  display: (o: string) => string;
  allChecked: boolean;
}) {
  const filtered = options.filter((o) =>
    display(o).toLowerCase().includes(search.toLowerCase()),
  );

  // How tall can the list be? Clamp between 120 and PANEL_MAX_H minus fixed rows.
  const fixedH = PANEL_HEAD_H + PANEL_FOOT_H + 8;
  const idealListH = Math.min(
    filtered.length * PANEL_ITEM_H + 44 /* select-all row + divider */,
    PANEL_MAX_H - fixedH,
  );
  const listH = Math.max(120, idealListH);

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 99999,
    left: coords.left,
    width: coords.width,
    minWidth: 260,
    ...(coords.openUpward
      ? { bottom: window.innerHeight - coords.top }
      : { top: coords.top }),
  };

  return ReactDOM.createPortal(
    <div
      style={panelStyle}
      className="rounded-xl border border-ink-100 bg-surface shadow-xl"
      // Stop clicks inside the panel from bubbling to the document listener
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Search */}
      <div className="border-b border-ink-100 p-2">
        <input
          autoFocus
          placeholder="Buscar..."
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          className="h-9 w-full rounded-lg border border-ink-200 bg-surface px-3 text-sm text-ink-800 outline-none focus:ring-2 focus:ring-brand-200"
        />
      </div>

      {/* Options list */}
      <div style={{ maxHeight: listH, overflowY: 'auto' }} className="p-2">
        <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50">
          <input
            type="checkbox"
            checked={allChecked}
            onChange={onToggleAll}
            className="h-4 w-4 rounded border-ink-300"
          />
          Seleccionar todo
        </label>
        <div className="my-1 border-t border-ink-100" />
        {filtered.length === 0 ? (
          <p className="px-2 py-2 text-sm text-ink-400">Sin resultados.</p>
        ) : (
          filtered.map((o) => (
            <label
              key={o}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-ink-700 hover:bg-ink-50"
            >
              <input
                type="checkbox"
                checked={pending.includes(o)}
                onChange={() => onToggleOne(o)}
                className="h-4 w-4 rounded border-ink-300"
              />
              {display(o)}
            </label>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-2 border-t border-ink-100 p-2">
        <Btn variant="outline" onClick={onCancel}>
          Cancelar
        </Btn>
        <Btn onClick={onApply}>Aplicar</Btn>
      </div>
    </div>,
    document.body,
  );
}

// ─── Public component ─────────────────────────────────────────────────────────
export function ComparativosMultiSelect({
  label,
  options,
  selected,
  onApply,
  renderLabel,
  allLabel = '(Todas)',
  selectedSuffix = 'seleccionadas',
}: {
  label: string;
  options: string[];
  selected: string[];
  onApply: (next: string[]) => void;
  renderLabel?: (option: string) => string;
  allLabel?: string;
  selectedSuffix?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string[]>(selected);
  const [search, setSearch] = useState('');
  const [coords, setCoords] = useState<PanelCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const display = renderLabel ?? ((o: string) => o);

  // Close on outside click
  const handleDocMouseDown = useCallback(
    (e: MouseEvent) => {
      if (open && triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    },
    [open],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, [handleDocMouseDown]);

  // Re-compute coords on scroll / resize while panel is open
  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (triggerRef.current) setCoords(computeCoords(triggerRef.current));
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const openPanel = () => {
    if (!triggerRef.current) return;
    setPending(selected.length === 0 ? [...options] : selected);
    setSearch('');
    setCoords(computeCoords(triggerRef.current));
    setOpen(true);
  };

  const allChecked = pending.length === options.length && options.length > 0;
  const toggleAll = () => setPending(allChecked ? [] : [...options]);
  const toggleOne = (o: string) =>
    setPending((prev) =>
      prev.includes(o) ? prev.filter((x) => x !== o) : [...prev, o],
    );

  const handleApply = () => {
    onApply(pending.length === options.length ? [] : pending);
    setOpen(false);
  };

  const summaryLabel =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? display(selected[0])
        : `${selected.length} ${selectedSuffix}`;

  return (
    <div className="relative">
      <span className="mb-1.5 block text-sm font-medium text-ink-700">{label}</span>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        className="h-[50px] w-full rounded-xl border border-ink-200 bg-surface px-4 text-left text-[15px] text-ink-800 outline-none focus:ring-2 focus:ring-brand-200"
      >
        {summaryLabel}
      </button>

      {open && coords && (
        <DropdownPortal
          coords={coords}
          options={options}
          pending={pending}
          search={search}
          onSearch={setSearch}
          onToggleAll={toggleAll}
          onToggleOne={toggleOne}
          onCancel={() => setOpen(false)}
          onApply={handleApply}
          display={display}
          allChecked={allChecked}
        />
      )}
    </div>
  );
}
